import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    const initialTheme = savedTheme || 'light';

    const root = document.documentElement;
    if (initialTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    return initialTheme;
  };

  const [theme, setThemeState] = useState(getInitialTheme);

  // Mirrors the latest requested theme synchronously, so queued wipes and the error-revert each flip exactly once
  const themeRef = useRef(theme);
  const activeWipes = useRef(0);

  const commitTheme = useCallback((nextTheme) => {
    themeRef.current = nextTheme;
    setThemeState(nextTheme);
  }, []);

  const setTheme = useCallback(
    (nextTheme) => {
      if (nextTheme !== 'light' && nextTheme !== 'dark') return;
      // UserContext syncs the saved appearance right after a click; let the pending wipe apply it
      if (activeWipes.current > 0 && nextTheme === themeRef.current) return;
      commitTheme(nextTheme);
    },
    [commitTheme]
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // With an {x, y} origin the new theme wipes outward from that point (View Transitions API);
  // without one, or when unsupported / reduced motion, it swaps instantly. Returns the requested theme.
  const toggleTheme = (origin) => {
    const flip = () => (themeRef.current === 'dark' ? 'light' : 'dark');
    const canWipe =
      origin &&
      typeof document.startViewTransition === 'function' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const requested = flip();
    if (!canWipe) {
      commitTheme(requested);
      return requested;
    }
    themeRef.current = requested;

    const root = document.documentElement;
    const { x, y } = origin;
    const radius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    activeWipes.current += 1;
    root.classList.add('theme-vt');
    const transition = document.startViewTransition(() => {
      // The snapshot is taken as soon as this returns, so the class and React state must commit now.
      const nextTheme = themeRef.current;
      root.classList.toggle('dark', nextTheme === 'dark');
      flushSync(() => commitTheme(nextTheme));
    });
    transition.ready
      .then(() =>
        root.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
          { duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', pseudoElement: '::view-transition-new(root)' }
        )
      )
      .catch(() => {});
    transition.finished
      .catch(() => {})
      .then(() => {
        activeWipes.current -= 1;
        if (activeWipes.current === 0) root.classList.remove('theme-vt');
      });
    return requested;
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

